import { Button, IconButton, Typography, Chip, Avatar, TableSortLabel, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ErrorIcon from '@mui/icons-material/Error';
import ImageIcon from '@mui/icons-material/Image';
import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { mainPageRootStyle } from '../styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { deletePost, loadPostsByHandle } from './actions';
import HandleSelector from '../components/HandleSelector';
import EmptyState from '../components/EmptyState';
import Breadcrumb from '../components/Breadcrumb';
import ConfirmDialog from '../components/ConfirmDialog';

function PostsListPage() {
    const dispatch = useDispatch();
    const handles = useSelector(state => state.handle.handles);
    const posts = useSelector(state => state.scheduler.postsByHandle);
    const userId = useSelector(state => state.auth.user?.uid);
    const navigate = useNavigate();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [postToDelete, setPostToDelete] = useState(null);
    const [orderBy, setOrderBy] = useState('scheduledFor');
    const [order, setOrder] = useState('asc');

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const selectedHandle = queryParams.get('handle');

    useEffect(() => {
        document.title = 'Post Scheduler - Sky Toolbox';
    }, []);

    // Auto-select first handle if none is selected
    useEffect(() => {
        if (!selectedHandle && handles && handles.length > 0) {
            navigate(`/scheduler?handle=${handles[0].handle}`, { replace: true });
        }
    }, [selectedHandle, handles, navigate]);

    useEffect(() => {
        dispatch(loadPostsByHandle(userId, selectedHandle));
    }, [dispatch, selectedHandle, userId]);

    const handleDelete = (id) => {
        const postToDelete = posts.find(p => p.id === id);
        setPostToDelete(postToDelete);
        setDeleteDialogOpen(true);
    };

    const confirmDelete = () => {
        if (postToDelete) {
            dispatch(deletePost(postToDelete.id));
        }
        setDeleteDialogOpen(false);
        setPostToDelete(null);
    };

    const cancelDelete = () => {
        setDeleteDialogOpen(false);
        setPostToDelete(null);
    };

    const handleSelectedHandleChange = (event) => {
        navigate(`/scheduler?handle=${event.target.value}`);
    };

    const handleAddPost = () => {
        navigate(`/scheduler/add?handle=${selectedHandle}`);
    };

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const getStatusIcon = (status) => {
        switch (status?.toLowerCase()) {
            case 'published':
                return <CheckCircleIcon sx={{ fontSize: '1rem' }} />;
            case 'failed':
                return <ErrorIcon sx={{ fontSize: '1rem' }} />;
            default:
                return <ScheduleIcon sx={{ fontSize: '1rem' }} />;
        }
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'published':
                return 'success';
            case 'failed':
                return 'error';
            case 'pending':
                return 'warning';
            default:
                return 'default';
        }
    };

    const sortedPosts = useMemo(() => {
        if (!posts || posts.length === 0) return [];
        
        const sorted = [...posts].sort((a, b) => {
            let aValue, bValue;
            
            if (orderBy === 'scheduledFor') {
                aValue = a.scheduledFor ? new Date(a.scheduledFor).getTime() : 0;
                bValue = b.scheduledFor ? new Date(b.scheduledFor).getTime() : 0;
            } else if (orderBy === 'status') {
                aValue = a.status || '';
                bValue = b.status || '';
            } else if (orderBy === 'content') {
                aValue = (a.content || a.text || '').toLowerCase();
                bValue = (b.content || b.text || '').toLowerCase();
            } else {
                return 0;
            }
            
            if (order === 'asc') {
                return aValue > bValue ? 1 : -1;
            } else {
                return aValue < bValue ? 1 : -1;
            }
        });
        
        return sorted;
    }, [posts, orderBy, order]);

    return (
        <Paper style={mainPageRootStyle} sx={{ p: 3 }}>
            <Breadcrumb items={[
                { label: 'Post Scheduler' }
            ]} />
            
            {/* Page Header */}
            <Box 
                sx={{ 
                    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                    borderRadius: 3,
                    p: 3,
                    mb: 4,
                    color: 'white',
                }}
            >
                <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 1 }}>
                    📅 Post Scheduler
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    Plan and schedule your BlueSky posts in advance
                </Typography>
            </Box>

            <Box display="flex" justifyContent="center" flexWrap="wrap" gap={2} sx={{ mb: 4 }}>
                <HandleSelector
                    value={selectedHandle}
                    onChange={handleSelectedHandleChange}
                    handles={handles}
                    ariaLabel="Select handle to view scheduled posts"
                />
                <Button 
                    variant="contained" 
                    size="large"
                    onClick={handleAddPost}
                    disabled={!selectedHandle}
                    sx={{ 
                        minWidth: 200,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #5568d3 0%, #63398b 100%)',
                        }
                    }}
                >
                    Schedule New Post
                </Button>
            </Box>

            {(!posts || posts.length === 0) ? (
                <EmptyState
                    title="No scheduled posts"
                    message="Create your first scheduled post to get started"
                />
            ) : (
                <TableContainer component={Paper} sx={{ elevation: 3 }}>
                    <Table sx={{ minWidth: 650 }} aria-label="scheduled posts table">
                        <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableRow>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'content'}
                                        direction={orderBy === 'content' ? order : 'asc'}
                                        onClick={() => handleRequestSort('content')}
                                    >
                                        <strong>Content</strong>
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell><strong>Images</strong></TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'scheduledFor'}
                                        direction={orderBy === 'scheduledFor' ? order : 'asc'}
                                        onClick={() => handleRequestSort('scheduledFor')}
                                    >
                                        <strong>Scheduled For</strong>
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'status'}
                                        direction={orderBy === 'status' ? order : 'asc'}
                                        onClick={() => handleRequestSort('status')}
                                    >
                                        <strong>Status</strong>
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="right"></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedPosts.map((post) => (
                                <TableRow
                                    key={post.id}
                                    sx={{ 
                                        '&:last-child td, &:last-child th': { border: 0 },
                                        '&:hover': { 
                                            bgcolor: 'action.hover',
                                        }
                                    }}
                                >
                                    <TableCell sx={{ whiteSpace: 'pre-wrap', maxWidth: 400 }}>
                                        {(post.content || post.text) || 'No content'}
                                    </TableCell>
                                    <TableCell>
                                        {post.imageUrls && post.imageUrls.length > 0 ? (
                                            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                                {post.imageUrls.slice(0, 3).map((url, index) => (
                                                    <Tooltip key={index} title="Click to view full size" arrow>
                                                        <Avatar
                                                            src={url}
                                                            variant="rounded"
                                                            sx={{ 
                                                                width: 60, 
                                                                height: 60,
                                                                cursor: 'pointer',
                                                                '&:hover': {
                                                                    opacity: 0.8,
                                                                    transform: 'scale(1.05)',
                                                                    transition: 'all 0.2s'
                                                                }
                                                            }}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                window.open(url, '_blank');
                                                            }}
                                                        >
                                                            <ImageIcon />
                                                        </Avatar>
                                                    </Tooltip>
                                                ))}
                                                {post.imageUrls.length > 3 && (
                                                    <Chip 
                                                        label={`+${post.imageUrls.length - 3}`} 
                                                        size="small"
                                                        sx={{ height: 20, fontSize: '0.7rem' }}
                                                    />
                                                )}
                                            </Box>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                No images
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {post.scheduledFor ? new Date(post.scheduledFor).toLocaleString() : 'N/A'}
                                    </TableCell>
                                    <TableCell>
                                        <Chip 
                                            icon={getStatusIcon(post.status)}
                                            label={post.status || 'pending'}
                                            color={getStatusColor(post.status)}
                                            size="small"
                                            sx={{ textTransform: 'capitalize' }}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton 
                                            onClick={() => navigate(`/scheduler/edit/${post.id}?handle=${selectedHandle}`)}
                                            sx={{ 
                                                color: 'primary.main',
                                                '&:hover': { bgcolor: 'primary.light', color: 'primary.dark' }
                                            }}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton 
                                            onClick={() => handleDelete(post.id)}
                                            sx={{ 
                                                color: 'error.main',
                                                '&:hover': { bgcolor: 'error.light', color: 'error.dark' }
                                            }}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <ConfirmDialog
                open={deleteDialogOpen}
                onClose={cancelDelete}
                onConfirm={confirmDelete}
                title="Delete Post"
                message="Are you sure you want to delete this scheduled post? This action cannot be undone."
                confirmText="Delete"
                cancelText="Cancel"
                confirmColor="error"
                autoFocusConfirm={true}
            />
        </Paper>
    );
}

export default PostsListPage;
